import React, { useState, useRef, useEffect } from "react";
import Button from "../../components/LoaderButton";
import logo from "../../assets/logo.svg";
import Back from "../../assets/back-arrow.svg";
import { useTranslation } from "react-i18next";

interface OtpVerificationProps {
  loader: boolean;
  handleSubmit: any;
  handleBack?: any;
  error?: any;
  handleGetOtpThroughMail?: any;
  methodType?: string;
  setTimeLeft?: any;
  timeLeft?: any;
  username: any;
  className?: string;
  hideLogo?: boolean;
}

const OtpVerification: React.FC<OtpVerificationProps> = ({
  loader,
  handleSubmit,
  handleBack,
  error,
  handleGetOtpThroughMail,
  methodType,
  setTimeLeft,
  timeLeft,
  username,
  className = "",
  hideLogo = false,
}) => {
  const [otp, setOtp] = useState(Array(6).fill(""));
  const otpLength = 6;

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, otpLength);
    const newOtp = pastedData.split("");
    setOtp(newOtp);

    newOtp.forEach((digit, index) => {
      const inputElement = inputRefs.current[index];
      if (inputElement) {
        inputElement.value = digit;
      }
    });

    const nextInputIndex =
      newOtp.length < otpLength ? newOtp.length : otpLength - 1;
    inputRefs.current[nextInputIndex]?.focus();
  };

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  const handleChange = (e: any, index: number) => {
    const { value } = e.target;
    if (value === "") {
      const newOtp = [...otp];
      newOtp[index] = "";
      if (index > 0 && inputRefs.current[index - 1]) {
        inputRefs.current[index - 1]?.focus();
      }
      setOtp(newOtp);
    } else if (/^\d$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (index < otp?.length - 1 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && otp[index] === "" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter") {
      handleSubmit(otp);
    }
  };

  useEffect(() => {
    if (inputRefs?.current[0]) {
      inputRefs?.current[0].focus();
    }
  }, []);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  return (
    <div
      className={`relative ${
        className ? className : "w-full md:w-1/2 md:px-12 sm:p-10 p-4"
      } flex flex-col justify-center items-center bg-white`}
    >
      {hideLogo ? (
        <div className="w-full flex items-center justify-start -mt-3 -ml-3 rtl:-mr-3 rtl:ml-0 mb-3">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1.5 text-xs text-[#728385] hover:text-[#173e43] transition-colors cursor-pointer py-1 px-1 rounded hover:bg-gray-50"
          >
            <img src={Back} alt="Back" className="w-3.5 h-3.5 object-contain inline" />
            <span>{t("SignUp.backTo", "Back")}</span>
          </button>
        </div>
      ) : (
        <div className="mb-4 relative w-full flex justify-center items-center">
          <img
            src={Back}
            alt="Back"
            className="absolute left-2 cursor-pointer w-4 h-4"
            onClick={handleBack}
          />
          <img src={logo} alt="Logo" className="h-10 object-contain" />
        </div>
      )}

      <div className="mb-3 px-2 text-center">
        <h2 className="text-xl md:text-2xl font-bold text-purple mb-0.5">
          {methodType === "MFA"
            ? t("SignUp.enterYourVerficationCode")
            : t("SignUp.enterYourVerficationCodeFromMail")}
        </h2>
        <p className="text-grey max-w-[320px] text-xs md:text-sm">
          {t("SignUp.toIncreaseSecurity")}
        </p>
      </div>

      <div className="flex w-full justify-between gap-1.5 sm:gap-2">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="number"
            maxLength={1}
            value={digit}
            onPaste={handlePaste}
            className="w-full max-w-[46px] h-[46px] sm:max-w-[50px] sm:h-[50px] bg-lightgrey p-1 text-center text-xl sm:text-2xl font-bold rounded focus:outline-none focus:ring-1 focus:ring-purple"
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onFocus={(e) => e.target.select()}
          />
        ))}
      </div>

      {username && methodType === "MFA" && (
        <div
          onClick={handleGetOtpThroughMail}
          className="hover:text-purple-400 underline mt-2.5 mb-1 w-full text-purple text-xs font-bold text-center cursor-pointer"
        >
          <span className="p-0.5">{t("SignUp.getTheOtp")}</span>
        </div>
      )}

      {error && (
        <div className="my-1.5 w-full text-red-600 text-xs text-center">
          {error}
        </div>
      )}

      <div className="mt-3 w-full">
        <Button
          text={t("SignUp.verify")}
          onClick={() => handleSubmit(otp)}
          isLoading={loader}
          disabled={loader}
        />
      </div>

      {username &&
        (methodType === "EMAIL_OTP" || methodType === "PASSWORD_RESET_OTP") && (
          <div className="mt-2 text-grey text-xs">
            {timeLeft === 0 ? (
              <p
                className="text-grey text-xs cursor-pointer hover:underline"
                onClick={handleGetOtpThroughMail}
              >
                {t("SignUp.requestCodeAgain")}
              </p>
            ) : (
              <p className="text-grey text-xs">
                {t("SignUp.requestCodeAgain")}{" "}
                <b className="text-black">{formatTime(timeLeft)}</b>
              </p>
            )}
          </div>
        )}
    </div>
  );
};

export default OtpVerification;
